import React, { useCallback, useMemo } from 'react';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';

import type { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants';

import { useEntryPoint } from '@atlassian/jira-entry-point';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { jsmUpsellModalEntryPoint } from '@atlassian/jira-jsm-upsell-modal/entrypoint';
import { isJSMFastFollowExperimentVariation } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness-jsm-fast-follow-free-to-standard-modal';
import { editionAwarenessJsmFastFollowModalEntryPoint } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness-jsm-fast-follow-free-to-standard-modal/entrypoint';
import { editionAwarenessJsmModalEntryPoint } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness-jsm-free-to-standard-modal/entrypoint';
import { useIsProductUsingCcp } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness-utils';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { SparkleIcon } from '@atlassian/jira-product-edition-upgrade';

import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	AnimatedButtonContainer,
	ButtonText,
	EATopNavAnimatedButtonContainer,
	EATopNavButtonText,
	EATopNavPillTheme,
	IconWrapper,
	PillTheme,
} from '../../../common/ui/styled';
import { type EALocation, TOP_NAV } from '../../../common/ui/types';

import messages from './messages';

export type EditionAwarenessCtaButtonProps = {
	projectType: typeof SERVICE_DESK_PROJECT;
	location: EALocation;
};

export function EditionAwarenessCtaButton(props: EditionAwarenessCtaButtonProps) {
	const { projectType, location } = props;

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isUsingCcp = useIsProductUsingCcp(projectType);
	const cloudId = useCloudId();

	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [config] = UNSAFE_noExposureExp('jsw_jsm_edition_awareness_top_nav_consolidation');
	const eaLocation = useMemo(() => config.get<string>('location', 'not-enrolled'), [config]);

	let productEntryPoint;
	let productEntryPointParam = {};
	// jsw_jsm_edition_awareness_top_nav_consolidation experiment
	if (eaLocation === TOP_NAV) {
		productEntryPoint = jsmUpsellModalEntryPoint;
		productEntryPointParam = { cloudIds: [cloudId] };
	} else if (isJSMFastFollowExperimentVariation()) {
		productEntryPoint = editionAwarenessJsmFastFollowModalEntryPoint;
	} else {
		productEntryPoint = editionAwarenessJsmModalEntryPoint;
	}

	// This rule is being followed, it's just not detected by the linter when it's referenced dynamically like this
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const entryPoint = useEntryPoint(productEntryPoint, productEntryPointParam);
	const triggerRef = useEntryPointButtonTrigger(entryPoint.entryPointActions);

	const fireButtonAnalytics = useCallback(
		(action: string) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action,
					actionSubject: 'button',
				}),
				'editionAwarenessFreePill',
				{
					location,
					experimentLocation: eaLocation,
				},
			);
		},
		[createAnalyticsEvent, location, eaLocation],
	);

	const onClick = useCallback(() => {
		fireButtonAnalytics('clicked');
	}, [fireButtonAnalytics]);

	const onHoverButton = useCallback(() => {
		fireButtonAnalytics('hovered');
	}, [fireButtonAnalytics]);

	const shouldUseTopNavigationStyles = location === TOP_NAV;
	const Container = shouldUseTopNavigationStyles
		? EATopNavAnimatedButtonContainer
		: AnimatedButtonContainer;
	const TextContainer = shouldUseTopNavigationStyles ? EATopNavButtonText : ButtonText;

	let entryPointSubject;
	if (eaLocation === TOP_NAV) {
		entryPointSubject = (
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPoint.entryPointReferenceSubject}
				id="jsm-upsell-modal"
				packageName="edition-awareness"
				teamName="Flywheel-Tako"
				runtimeProps={{}}
				entryPointActions={entryPoint.entryPointActions}
			/>
		);
	} else {
		entryPointSubject = (
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPoint.entryPointReferenceSubject}
				id="edition-awareness-modal"
				packageName="edition-awareness"
				teamName="Flywheel-Tako"
				// The TS error is due to the conditional entry point
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore-error
				runtimeProps={{ isUsingCcp }}
				entryPointActions={entryPoint.entryPointActions}
			/>
		);
	}
	return (
		<>
			<Container>
				<CustomThemeButton
					iconBefore={
						<IconWrapper>
							<SparkleIcon label="" />
						</IconWrapper>
					}
					onClick={onClick}
					theme={shouldUseTopNavigationStyles ? EATopNavPillTheme : PillTheme}
					onMouseEnter={onHoverButton}
					ref={triggerRef}
					interactionName="edition-awareness-free-pill"
					testId="navigation-apps-sidebar-edition-awareness.ui.free-pill.edition-awareness-cta-button.edition-awareness-free-pill-cta-button"
				>
					<TextContainer>{formatMessage(messages.upgradeToStandardCtaLabel)}</TextContainer>
				</CustomThemeButton>
			</Container>
			{entryPointSubject}
		</>
	);
}
